import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, apiGet } from "../../api";
import { Button } from "../../components/ui/button";
import { Copy, Pencil, Plus, Trash2, Zap as Bolt } from "lucide-react";
import { Modal, StatusPill } from "./shared";

/** Suppliers tab: list, activate/deactivate, regenerate token, manage fast-track tokens. */
export default function SuppliersTab() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newToken, setNewToken] = useState(null);
  const [fastTrackFor, setFastTrackFor] = useState(null); // {id, name} | null
  const [editingSupplier, setEditingSupplier] = useState(null); // supplier object | null

  const load = useCallback(() => {
    setLoading(true);
    apiGet("/portal/admin/suppliers").then(d => setSuppliers(d?.suppliers || [])).catch(() => toast.error("Failed to load suppliers")).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const portalUrl = (token) => `${window.location.origin}/portal?token=${token}`;

  const copyUrl = (token) => {
    navigator.clipboard.writeText(portalUrl(token));
    toast.success("Portal URL copied");
  };

  const toggleActive = async (sup) => {
    try {
      const action = sup.status === "active" ? "deactivate" : "activate";
      await axios.post(`${API}/portal/admin/suppliers/${sup.id}/${action}`);
      toast.success(`Supplier ${action}d`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const regenToken = async (sup) => {
    if (!window.confirm(`Regenerate token for ${sup.name}? The current link will stop working immediately.`)) return;
    try {
      const res = await axios.post(`${API}/portal/admin/suppliers/${sup.id}/regenerate-token`);
      setNewToken({ name: sup.name, token: res.data.new_token });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Regenerate failed");
    }
  };

  return (
    <div className="card" data-testid="suppliers-tab">
      {loading ? <p>Loading…</p> : suppliers.length === 0 ? (
        <p style={{ color: "var(--octopus-voltage)", opacity: 0.7 }}>
          No suppliers configured. Add one in the <strong>Settings</strong> tab.
        </p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="data-table" data-testid="suppliers-table">
            <thead>
              <tr>
                <th>Supplier</th><th>Category</th><th>Contact</th>
                <th>Portal URL</th><th>Status</th><th>Last Submission</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map(s => (
                <tr key={s.id} data-testid={`supplier-row-${s.id}`}>
                  <td><strong>{s.name}</strong></td>
                  <td>{s.category}</td>
                  <td style={{ fontSize: "0.85rem" }}>{s.contact_email}</td>
                  <td>
                    {s.token ? (
                      <button onClick={() => copyUrl(s.token)} style={{ background: "transparent", border: "1px solid rgba(96,240,248,0.15)", padding: "0.25rem 0.5rem", borderRadius: "4px", color: "var(--octopus-voltage)", cursor: "pointer", fontSize: "0.75rem", fontFamily: "monospace", display: "inline-flex", alignItems: "center", gap: "0.3rem" }} data-testid={`copy-url-${s.id}`}>
                        …{s.token.slice(-8)} <Copy size={11} />
                      </button>
                    ) : (
                      <span style={{ fontSize: "0.75rem", color: "var(--color-bg-purple-haze, #A49FC8)" }}>—</span>
                    )}
                  </td>
                  <td><StatusPill status={s.status} /></td>
                  <td style={{ fontSize: "0.85rem" }}>{s.last_submission_at ? new Date(s.last_submission_at).toLocaleDateString() : "—"}</td>
                  <td>
                    <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                      <button onClick={() => setEditingSupplier(s)} data-testid={`edit-supplier-${s.id}`} style={{ background: "rgba(96,240,248,0.08)", border: "1px solid rgba(96,240,248,0.25)", cursor: "pointer", color: "var(--octopus-voltage)", borderRadius: "4px", padding: "0.2rem 0.55rem", fontSize: "0.75rem", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: "0.3rem" }}><Pencil size={11} />Edit</button>
                      <button onClick={() => setFastTrackFor({ id: s.id, name: s.name })} data-testid={`open-fast-track-${s.id}`} style={{ background: "rgba(255,216,79,0.1)", border: "1px solid rgba(255,216,79,0.35)", cursor: "pointer", color: "#FFD84F", borderRadius: "4px", padding: "0.2rem 0.55rem", fontSize: "0.75rem", fontWeight: 500 }}>Tokens</button>
                      <button onClick={() => regenToken(s)} data-testid={`regen-token-${s.id}`} style={{ background: "rgba(96,240,248,0.08)", border: "1px solid rgba(96,240,248,0.25)", cursor: "pointer", color: "var(--octopus-voltage)", borderRadius: "4px", padding: "0.2rem 0.55rem", fontSize: "0.75rem", fontWeight: 500 }}>New Link</button>
                      <button
                        onClick={() => toggleActive(s)}
                        data-testid={`toggle-active-${s.id}`}
                        style={{
                          background: s.status === "active" ? "rgba(16,195,149,0.1)" : "rgba(164,159,200,0.08)",
                          border: `1px solid ${s.status === "active" ? "rgba(16,195,149,0.35)" : "rgba(164,159,200,0.25)"}`,
                          cursor: "pointer",
                          color: s.status === "active" ? "#10C395" : "#A49FC8",
                          borderRadius: "4px",
                          padding: "0.2rem 0.55rem",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        {s.status === "active" ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {newToken && (
        <Modal onClose={() => setNewToken(null)}>
          <div data-testid="new-token-modal" style={{ maxWidth: "600px" }}>
            <h3 style={{ marginBottom: "0.75rem" }}>New token for {newToken.name}</h3>
            <p style={{ color: "var(--octopus-voltage)", fontSize: "0.85rem", marginBottom: "1rem" }}>
              The previous link has stopped working. Copy the new URL before closing:
            </p>
            <code style={{ display: "block", padding: "0.75rem", background: "rgba(10,18,42,0.6)", borderRadius: "6px", fontSize: "0.85rem", wordBreak: "break-all", marginBottom: "1rem" }}>
              {portalUrl(newToken.token)}
            </code>
            <Button onClick={() => { navigator.clipboard.writeText(portalUrl(newToken.token)); toast.success("Copied"); setNewToken(null); }} className="btn btn-primary">
              <Copy size={14} style={{ marginRight: "0.4rem" }} /> Copy URL & close
            </Button>
          </div>
        </Modal>
      )}

      {fastTrackFor && (
        <FastTrackTokensModal supplier={fastTrackFor} onClose={() => setFastTrackFor(null)} />
      )}

      {editingSupplier && (
        <EditSupplierModal supplier={editingSupplier} onClose={() => setEditingSupplier(null)} onSaved={load} />
      )}
    </div>
  );
}


function EditSupplierModal({ supplier, onClose, onSaved }) {
  const [name, setName] = useState(supplier.name);
  const [category, setCategory] = useState(supplier.category);
  const [contactEmail, setContactEmail] = useState(supplier.contact_email);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet("/portal/admin/categories-allowed").then(d => setCategories(d?.categories || [])).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!name.trim() || !category.trim() || !contactEmail.trim()) {
      toast.error("All fields are required");
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/portal/admin/suppliers/${supplier.id}`, {
        name: name.trim(),
        category: category.trim(),
        contact_email: contactEmail.trim(),
      });
      toast.success("Supplier updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div data-testid="edit-supplier-modal" style={{ width: "420px", maxWidth: "100%" }}>
        <h3 style={{ marginBottom: "1.25rem", color: "var(--octopus-ice)" }}>
          <Pencil size={15} style={{ display: "inline", marginRight: "0.45rem", verticalAlign: "middle" }} />
          Edit supplier
        </h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div>
            <label className="form-label">Supplier Name *</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              className="form-input"
              data-testid="edit-supplier-name"
            />
          </div>
          <div>
            <label className="form-label">Category *</label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="form-input"
              data-testid="edit-supplier-category"
            >
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
              {!categories.includes(category) && category && (
                <option value={category}>{category}</option>
              )}
            </select>
          </div>
          <div>
            <label className="form-label">Contact Email *</label>
            <input
              type="email"
              value={contactEmail}
              onChange={e => setContactEmail(e.target.value)}
              className="form-input"
              data-testid="edit-supplier-email"
            />
          </div>
        </div>
        <div style={{ display: "flex", gap: "0.6rem", marginTop: "1.5rem", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ background: "transparent", border: "1px solid rgba(96,240,248,0.2)", color: "var(--octopus-voltage)", borderRadius: "6px", padding: "0.4rem 0.9rem", cursor: "pointer", fontSize: "0.85rem" }}>
            Cancel
          </button>
          <Button onClick={handleSave} disabled={saving} className="btn btn-primary" data-testid="edit-supplier-save">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}


/**
 * Modal for issuing and managing one-time fast-track auto-approval tokens
 * for a single supplier. Listed newest-first with status pills
 * (active/used/expired). Active tokens can be revoked; used tokens are
 * kept as an audit trail and show which submission consumed them.
 */
function FastTrackTokensModal({ supplier, onClose }) {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [latest, setLatest] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    apiGet(`/portal/admin/suppliers/${supplier.id}/fast-track-tokens`)
      .then(d => setTokens(d?.tokens || []))
      .catch(() => toast.error("Failed to load tokens"))
      .finally(() => setLoading(false));
  }, [supplier.id]);

  useEffect(load, [load]);

  const create = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/portal/admin/suppliers/${supplier.id}/fast-track-tokens`);
      setLatest(res.data?.token);
      toast.success("Token created");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create token");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (tokenId) => {
    if (!window.confirm("Revoke this token? It cannot be undone.")) return;
    try {
      await axios.delete(`${API}/portal/admin/fast-track-tokens/${tokenId}`);
      toast.success("Token revoked");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Revoke failed");
    }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Token copied"); };

  return (
    <Modal onClose={onClose}>
      <div data-testid="fast-track-modal" style={{ maxWidth: "640px" }}>
        <div style={{ marginBottom: "0.85rem" }}>
          <h3 style={{ marginBottom: "0.35rem", color: "var(--octopus-ice)", display: "flex", alignItems: "center", gap: "0.45rem" }}>
            <Bolt size={16} style={{ color: "#FFD84F" }} /> Fast-track tokens · {supplier.name}
          </h3>
          <p style={{ color: "var(--octopus-voltage)", fontSize: "0.82rem", margin: 0, opacity: 0.85 }}>
            One-time tokens that auto-approve a submission from <strong>{supplier.name}</strong>. Share
            with the supplier — when they paste it into their submission, it bypasses PM review and
            routes straight to the records updater. Expires in 30 days, single-use.
          </p>
        </div>

        {latest && (
          <div data-testid="fast-track-new-token" style={{
            margin: "0.85rem 0", padding: "0.85rem 1rem", borderRadius: "8px",
            background: "rgba(255,216,79,0.08)", border: "1px solid rgba(255,216,79,0.45)",
          }}>
            <div style={{ fontSize: "0.7rem", color: "#FFD84F", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.3rem" }}>New token — copy now</div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
              <code style={{ fontSize: "1.05rem", fontFamily: "monospace", color: "var(--octopus-ice)", letterSpacing: "0.02em" }}>{latest}</code>
              <Button onClick={() => copy(latest)} className="btn btn-secondary" style={{ padding: "0.25rem 0.6rem", fontSize: "0.78rem" }}>
                <Copy size={12} style={{ marginRight: "0.3rem" }} /> Copy
              </Button>
            </div>
          </div>
        )}

        <Button onClick={create} disabled={creating} className="btn btn-primary" data-testid="create-fast-track-btn" style={{ marginBottom: "1rem" }}>
          <Plus size={14} style={{ marginRight: "0.35rem" }} />
          {creating ? "Generating…" : "Generate new token"}
        </Button>

        {loading ? (
          <p style={{ opacity: 0.6 }}>Loading…</p>
        ) : tokens.length === 0 ? (
          <p style={{ color: "var(--octopus-voltage)", opacity: 0.7, fontSize: "0.85rem", textAlign: "center", padding: "1rem" }}>
            No fast-track tokens have been issued for this supplier yet.
          </p>
        ) : (
          <div style={{ maxHeight: "320px", overflowY: "auto", border: "1px solid rgba(96,240,248,0.15)", borderRadius: "6px" }}>
            <table className="data-table" data-testid="fast-track-tokens-table" style={{ marginBottom: 0, fontSize: "0.82rem" }}>
              <thead style={{ position: "sticky", top: 0, background: "rgba(10,18,42,0.98)" }}>
                <tr>
                  <th>Token</th>
                  <th style={{ textAlign: "center" }}>Status</th>
                  <th>Issued</th>
                  <th>Expires</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map(t => (
                  <tr key={t.id} data-testid={`fast-track-row-${t.id}`}>
                    <td style={{ fontFamily: "monospace", color: "var(--octopus-ice)" }}>{t.token}</td>
                    <td style={{ textAlign: "center" }}><FastTrackStatusPill status={t.status} /></td>
                    <td>{new Date(t.created_at).toLocaleDateString()}</td>
                    <td style={{ color: t.status === "expired" ? "#F050F8" : "var(--octopus-voltage)" }}>
                      {new Date(t.expires_at).toLocaleDateString()}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <div style={{ display: "inline-flex", gap: "0.4rem" }}>
                        {t.status === "active" && (
                          <>
                            <button onClick={() => copy(t.token)} title="Copy token" data-testid={`copy-token-${t.id}`} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--octopus-voltage)" }}><Copy size={13} /></button>
                            <button onClick={() => revoke(t.id)} title="Revoke" data-testid={`revoke-token-${t.id}`} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#F050F8" }}><Trash2 size={13} /></button>
                          </>
                        )}
                        {t.status === "used" && t.used_for_submission_id && (
                          <span title={`Used by submission ${t.used_for_submission_id.slice(0, 8)}`} style={{ fontSize: "0.7rem", color: "var(--octopus-voltage)", opacity: 0.65, fontFamily: "monospace" }}>
                            #{t.used_for_submission_id.slice(0, 8)}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}


function FastTrackStatusPill({ status }) {
  const cfg = {
    active:  { bg: "rgba(34,197,94,0.15)", color: "#86efac", border: "rgba(34,197,94,0.45)", label: "Active" },
    used:    { bg: "rgba(96,240,248,0.12)", color: "var(--octopus-ice)", border: "rgba(96,240,248,0.4)", label: "Used" },
    expired: { bg: "rgba(240,80,248,0.12)", color: "#F050F8", border: "rgba(240,80,248,0.4)", label: "Expired" },
  }[status] || { bg: "rgba(96,240,248,0.08)", color: "var(--octopus-voltage)", border: "rgba(96,240,248,0.2)", label: status };
  return (
    <span style={{
      padding: "0.15rem 0.55rem", borderRadius: "999px", fontSize: "0.7rem", fontWeight: 700,
      letterSpacing: "0.04em", background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
    }}>{cfg.label}</span>
  );
}
